import { useState } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { NavLink } from 'react-router';
import classNames from 'classnames';

import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { logout as serviceLogout } from '../../lib/store/common-actions';
import { switchAccount } from '../../lib/store/crm';
import { hasAccountPaidAndDemoPlan } from '../../lib/store/crm/helpers';
import { isUserVerifiedStatus } from '../../lib/store/selectors';
import { FeatureFlags } from '../../lib/util/flags/types';

import ArrowRight from '../../assets/img/chevron-right.png';
import { UserPhoto } from '../../components';
import AppVersion from '../../components/AppVersion/AppVersion';
import SwitchingAccountPopup from '../../components/SwitchingAccountPopup/SwitchingAccountPopup';
import config from '../../config';
import { useFlags } from '../../hooks';

import ChangePasswordModal from './ChangePassword/ChangePasswordModal';
import { getMenuList } from './helpers';
import { myProfileIds } from './MyProfile.constants';
import { MyProfileItem } from './MyProfile.types';

import styles from './MyProfile.module.scss';

type OwnProps = {};
type Props = OwnProps & ReduxProps;

const MyProfile = (props: Props) => {
  const {
    logout,
    switchToAccount,
    hasPaidAndDemoPlan,
    isUseDemoAccount,
    isUserVerified,
    individual,
  } = props;
  const dispatch = useDispatch();

  const [isSwitchingOtherAccount, setIsSwitchingOtherAccount] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const flags = useFlags([ FeatureFlags.referrals ]);

  const menu = getMenuList(
    hasPaidAndDemoPlan,
    isUseDemoAccount ? lang.commonSwitchToFunded() : lang.commonSwitchToDemo(),
    flags[FeatureFlags.referrals].enabled,
  ).slice(0, -1);
  const logoutMenuItem = getMenuList().slice(-1)[0];

  // Only dispatch logout - the BFF logout redirect brings the user back to the app,
  // where the normal unauthenticated flow takes over. Dispatching login() here would
  // start a competing navigation and cancel the logout redirect.
  const handleLogout = () => {
    logout();
  };

  const handleOptionClick = (id: string) => {
    if (id === myProfileIds.security) {
      openChangePasswordModal();
      return;
    }
    if (id === myProfileIds.helpCenter) {
      window.open(config.links.helpCenterUrl, '_blank');
    }
    if (id === myProfileIds.changePlan) {
      setIsSwitchingOtherAccount(true);
      switchToAccount(!isUseDemoAccount);
    }
  };

  const openChangePasswordModal = () => {
    setIsChangePasswordOpen(true);
  };
  const isDisabledLink = (item: MyProfileItem): boolean =>
    item.id === myProfileIds.documents && !isUserVerified;

  return (
    <div className={styles.wrapper}>
      <div className={styles.userWrapper}>
        <div className={styles.user}>
          <UserPhoto
            isHeader
            fullSize
            first_name={individual?.first_name}
            last_name={individual?.last_name}
          />
        </div>
        <div className={styles.userInfo}>
          <div>
            <span
              className={styles.name}
            >
              {`${individual?.first_name} ${individual?.last_name}`}
            </span>
          </div>
          <span className={styles.email}>{individual?.email}</span>
        </div>
      </div>
      <div className={styles.menuWrapper}>
        {menu.map((item) => (
          <NavLink
            className={isDisabledLink(item) ? styles.linkDisabled : styles.link}
            id={item.id}
            key={item.id}
            to={{
              pathname: item?.path && !isDisabledLink(item) ? item.path : '',
            }}
            onClick={() => handleOptionClick(item.id)}
          >
            <div
              key={item.id}
              className={classNames(styles.item, {
                [styles.switchAccountItem]: item.id === myProfileIds.changePlan,
              })}
            >
              <div className={styles.imageAndName}>
                <div className={styles.imgageWrapper}>
                  <img
                    className={isDisabledLink(item) ? styles.imageDisabled : styles.image}
                    src={item.img}
                    alt={item.label}
                  />
                </div>
                <div className={styles.label}>{item.label}</div>
              </div>
              {item.id !== myProfileIds.changePlan && (
                <div className={styles.arrowWrapper}>
                  <img className={styles.arrow} src={ArrowRight} alt="arrow" />
                </div>
              )}
            </div>
          </NavLink>
        ))}
      </div>
      <div className={styles.menuWrapper}>
        <NavLink
          className={styles.link}
          id={logoutMenuItem.id}
          key={logoutMenuItem.id}
          onClick={() => handleLogout()}
          to=""
        >
          <div key={logoutMenuItem.id} className={classNames(styles.item, styles.logoutItem)}>
            <div className={styles.imageAndName}>
              <div className={styles.imgageWrapper}>
                <img className={styles.image} src={logoutMenuItem.img} alt={logoutMenuItem.label} />
              </div>
              <div className={styles.label}>{logoutMenuItem.label}</div>
            </div>
            <div className={styles.arrowWrapper}>
              <img className={styles.arrow} src={ArrowRight} alt="arrow" />
            </div>
          </div>
        </NavLink>
      </div>
      <SwitchingAccountPopup
        isDisplayModal={isSwitchingOtherAccount}
        setIsDisplayModal={setIsSwitchingOtherAccount}
      />
      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
      <AppVersion />
    </div>
  );
};

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
  dispatch,
  logout: () => dispatch(serviceLogout()),
  switchToAccount: (toDemo: boolean) => dispatch(switchAccount(toDemo)),
});

const mapStateToProps = (state: RootState) => ({
  isUserVerified: isUserVerifiedStatus(state.crm.individualExtendedInfo),
  individual: state.crm.individualExtendedInfo,
  isUseDemoAccount: state.crm.isUseDemoAccount,
  hasPaidAndDemoPlan: hasAccountPaidAndDemoPlan(state.crm),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connect(mapStateToProps, mapDispatchToProps)(MyProfile);
